/**
 * useOfflineQueue — EPIC 2 (Tickets 13 & 14)
 * Queues failed GPS tracking points in localStorage for automatic retry.
 *
 * T13: If /tracking/point fails → store locally, retry when online.
 * T14: Auto-sync on window 'online' event.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import config from 'config';

const API = config.API_URL;
const QUEUE_KEY = 'ygo_gps_offline_queue';
const MAX_QUEUE_SIZE = 200;

export function useOfflineQueue() {
  const [queueSize, setQueueSize] = useState(0);
  const [isFlushing, setIsFlushing] = useState(false);
  const flushingRef = useRef(false);

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    'Content-Type': 'application/json',
  });

  // Load queue from localStorage
  const getQueue = useCallback(() => {
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }, []);

  // Save queue to localStorage
  const saveQueue = useCallback((queue) => {
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
      setQueueSize(queue.length);
    } catch {
      // Storage full — drop oldest items
      const trimmed = queue.slice(-MAX_QUEUE_SIZE / 2);
      localStorage.setItem(QUEUE_KEY, JSON.stringify(trimmed));
      setQueueSize(trimmed.length);
    }
  }, []);

  // T13: Enqueue a failed GPS point
  const enqueue = useCallback((point) => {
    const queue = getQueue();
    if (queue.length >= MAX_QUEUE_SIZE) {
      queue.shift(); // Drop oldest
    }
    queue.push({ ...point, queued_at: new Date().toISOString() });
    saveQueue(queue);
  }, [getQueue, saveQueue]);

  // T14: Flush queue — send all queued points to backend
  const flush = useCallback(async () => {
    if (flushingRef.current) return;
    const queue = getQueue();
    if (queue.length === 0) return;

    flushingRef.current = true;
    setIsFlushing(true);

    const remaining = [];
    for (const point of queue) {
      try {
        await axios.post(`${API}/api/driver/tracking/point`, {
          lat: point.lat,
          lng: point.lng,
          accuracy: point.accuracy,
          task_id: point.task_id || undefined,
        }, { headers: getHeaders(), timeout: 10000 });
        // Wait a bit to respect rate limiting
        await new Promise(r => setTimeout(r, 500));
      } catch {
        remaining.push(point);
      }
    }

    saveQueue(remaining);
    flushingRef.current = false;
    setIsFlushing(false);
  }, [getQueue, saveQueue]);

  // T14: Auto-flush on network return
  useEffect(() => {
    const handleOnline = () => {
      // Small delay to let network stabilize
      setTimeout(() => flush(), 2000);
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [flush]);

  // Init: count existing queued items
  useEffect(() => {
    setQueueSize(getQueue().length);
  }, [getQueue]);

  return {
    enqueue,
    flush,
    queueSize,
    isFlushing,
    hasQueued: queueSize > 0,
  };
}
